import queryString from 'querystring';

import React from 'react';

import { useTranslation } from 'react-i18next';
import { matchPath, NavLink, useHistory, useLocation, Link } from 'react-router-dom';

import DropdownItem from 'reactstrap/lib/DropdownItem';
import DropdownMenu from 'reactstrap/lib/DropdownMenu';
import DropdownToggle from 'reactstrap/lib/DropdownToggle';
import Nav from 'reactstrap/lib/Nav';
import NavItem from 'reactstrap/lib/NavItem';
// @ts-ignore
import UncontrolledDropdown from 'reactstrap/lib/UncontrolledDropdown';

import { Navigation, CategoryNavLinks, useAuth, useIntl } from '@ttstr/components';
import MenuPortal from '@ttstr/components/Navigation/MenuPortal';
import UserLoginModal from '@ttstr/components/Navigation/UserLoginModal';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { loggedIn, showLogin, setShowLogin, redirectAfterLogin } = useAuth();
  const { language } = useIntl();

  const qs = React.useMemo(() => {
    return queryString.parse(location.search.substring(1));
  }, [location.search]);

  const [mainMenuRef, setMainMenuRef] = React.useState<HTMLDivElement>(); // Could be put into Context
  const [customerMenuRef, setCustomerMenuRef] = React.useState<HTMLLIElement>(); // Could be put into Context
  const [navbarRef, setNavbarRef] = React.useState<HTMLUListElement>(); // Could be put into Context

  const toggleUserLoginModal = () => {
    setShowLogin(!showLogin);
  };

  const isRoot = React.useMemo(() => {
    const matches = matchPath(location.pathname, { path: '/', exact: true });
    return matches && matches.isExact;
  }, [location.pathname]);

  const mainMenu = (
    <>
      <NavItem>
        <NavLink to="/products" exact replace={isRoot} className="nav-link">
          {t(`NAVIGATION.ALL`)}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink to="/brands" exact replace={isRoot} className="nav-link">
          {t(`NAVIGATION.BRANDS`)}
        </NavLink>
      </NavItem>
      <NavItem>
        <NavLink to="/tickets" exact replace={isRoot} className="nav-link">
          Tickets
        </NavLink>
      </NavItem>
      <CategoryNavLinks
        excludeCategories={[
          3672,
          3974,
          4376,
          4378,
          4379,
          4372,
          4374,
          4382,
          4381,
          4377,
          4375,
          4370,
          4371,
          4503,
          4504,
          4507,
          4508,
          4509,
          4511,
          4510,
          4505,
          4506,
          4520,
        ]}
      />
      <NavItem>
        <a href="https://label.reaper-entertainment.com/" className="nav-link primary">
          {'Label'}
        </a>
      </NavItem>
      <NavItem>
        <a href="https://www.visionmerch.com/reaper-entertainment" className="nav-link primary">
          {'US Shop'}
        </a>
      </NavItem>
    </>
  );
  return (
    <header id="header" className="d-print-none sticky-top">
      <Navigation
        logo={require('./assets/images/logo.png')}
        // fixed="top"
        dark
        cartIcon={<i className="fal fa-shopping-bag" />}
        togglerIcon={
          <span className="hamburger-box">
            <span className="hamburger-inner" />
          </span>
        }
        togglerClassName="hamburger hamburger--squeeze d-xl-none"
        mainMenuRef={setMainMenuRef}
        customerMenuRef={setCustomerMenuRef}
        navbarRef={setNavbarRef}
        // alwaysCloseMainMenu (causes to autoclose nav on mobile when category dropdowns are opened)
        mainMenuColumnProps={{
          xs: { order: 1 },
        }}
        mainMenuPosition="right"
        showRegister={false}
        showLanguageSwitcher={true}
      />

      {/* Top Banner */}
      {language.includes('de') && (
        // only in this timeframe
        <div className="below-main-navigation container-fluid text-center bg-info py-3 shadow text-white">
          <strong>{'Ab sofort: VERSANDKOSTENFREIE Lieferung im Inland ab Bestellwert 100 €!'}</strong>
        </div>
      )}

      <MenuPortal target={navbarRef}>{mainMenu}</MenuPortal>

      <div className="below-main-navigation container-fluid text-center bg-info py-3 shadow d-none">
        {/* eslint-disable react/jsx-no-literals */}
        <Link to="/category/3645?tagId=4995">
          <strong>{t(`PROMO.TOPLINE`)}</strong>
        </Link>
        {/* eslint-enable react/jsx-no-literals */}
      </div>

      <MenuPortal target={mainMenuRef}>
        <Nav navbar className="d-xl-none">
          {mainMenu}
        </Nav>
      </MenuPortal>

      <MenuPortal target={customerMenuRef}>
        {loggedIn ? (
          <UncontrolledDropdown>
            <DropdownToggle nav caret title={t(`NAVIGATION.ACCOUNT`)} className="text-info">
              <i className="fal fa-user" /> <span className="sr-only">{t(`NAVIGATION.ACCOUNT`)}</span>
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem tag={NavLink} to="/customer/account" exact>
                {t(`CUSTOMER.TITLE`)}
              </DropdownItem>
              <DropdownItem tag={NavLink} to="/customer/orders" exact>
                {t(`ORDERS.TITLE`)}
              </DropdownItem>
              <DropdownItem tag={NavLink} to="/customer/landing-page" exact>
                {t(`CUSTOMER.LANDING_PAGE.TITLE`)}
              </DropdownItem>
              <DropdownItem divider />
              <DropdownItem tag={NavLink} to="/customer/logout">
                {t('CUSTOMER.LOGOUT')}
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        ) : (
          <NavItem tag="div">
            <NavLink
              to="#"
              isActive={() => false}
              onClick={(e) => {
                e.preventDefault();
                toggleUserLoginModal();
              }}
              className="nav-link"
              title={t(`NAVIGATION.ACCOUNT`)}
            >
              <i className="fas fa-user" /> <span className="sr-only">{t(`NAVIGATION.ACCOUNT`)}</span>
            </NavLink>
          </NavItem>
        )}
        <UserLoginModal
          show={showLogin}
          toggle={toggleUserLoginModal}
          onLoginSuccess={() =>
            redirectAfterLogin && history.push(typeof qs.success === 'string' ? qs.success : '/customer/landing-page')
          }
        >
          <div className="alert alert-default p-0">{t(`LOGIN.INTRO`)}</div>
        </UserLoginModal>
      </MenuPortal>
    </header>
  );
};

export default React.memo(Header);
